import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import findBillingContact from 'src/utils/findBillingContact';

const MAX_STRING_LENGTH = 150;

export const truncate = (value: string | number) => {
    let output = value?.toString() ?? '';

    if (output.length > MAX_STRING_LENGTH) {
        output = `${output.substring(0, MAX_STRING_LENGTH)}...`;
    }

    return output;
};

export const jsonTruncate = (obj: unknown) => {
    return truncate(JSON.stringify(obj, null, 2));
};

export const handleErrors = (error: any, defaultMessage = 'Something went wrong') => {
    try {
        if (error?.graphQLErrors) {
            return error?.graphQLErrors[0].message;
        }
    } catch (error) {
        return defaultMessage;
    }
};

export const camelCaseToCapitalizedWords = (camelCaseString: string) => {
    // split the string into an array of words
    const words = camelCaseString.split(/(?=[A-Z])/);

    // capitalize the first letter of each word and join with a space
    return words
        .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
};

export const enumToCamelCase = (value) => {
    if (value !== undefined) {
        if (value === 'NPI') {
            return value;
        }
        const words = value?.split('_');
        return words
            ?.map((word, index) => {
                const exceptionalUpperCases = ['NPI', 'MID', 'CS'];
                if (exceptionalUpperCases.includes(word)) {
                    return word;
                }
                if (index == 0) {
                    return word.charAt(0) + word.slice(1).toLowerCase();
                }
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(' ');
    }
    return '';
};

/**
 * Uses a country code to format a given phone number to be in the international format
 * @param {phoneNumber, countryCode} str The phone number and country code
 * @returns {string} A formatted phone number
 */
export const formatToInternational = (phoneNumber: string, countryCode: string): string => {
    const parsedNumber = parsePhoneNumberFromString(phoneNumber, countryCode as CountryCode);

    if (!parsedNumber) {
        return phoneNumber;
    }

    return parsedNumber.number;
};

export const FormatBillingDetails = (billingContactInformation) => {
    return {
        billingContact: `${billingContactInformation?.bci_firstName} ${billingContactInformation?.bci_lastName}`,
        billingAddress: `${billingContactInformation?.bci_ra_addressLine1},${billingContactInformation?.bci_ra_addressLine2},${billingContactInformation?.bci_ra_city},${billingContactInformation?.bci_ra_country},${billingContactInformation?.bci_ra_eircode}`,
        contactNumber: billingContactInformation?.bci_telephoneNumber,
        email: billingContactInformation?.bci_email,
    };
};
export const FormatMerchantBillingDetails = (billingContactInformation) => {
    const selectedPrincipal = findBillingContact(billingContactInformation.principals);
    return {
        billingContact: `${billingContactInformation?.aboutBusiness?.legalCompanyName}`,
        billingAddress: `${billingContactInformation?.aboutBusiness?.registeredAddress?.addressLine1},${billingContactInformation?.aboutBusiness?.registeredAddress?.addressLine2},${billingContactInformation?.aboutBusiness?.registeredAddress?.townOrCity},${billingContactInformation?.aboutBusiness?.registeredAddress?.country},${billingContactInformation?.aboutBusiness?.registeredAddress?.eircode}`,
        contactNumber: selectedPrincipal?.mobileNumber,
        email: selectedPrincipal?.email,
    };
};

// Turn enum into array
export const EnumToArray = (enumType) => {
    return Object.keys(enumType).map((key) => {
        return enumType[key];
    });
};
export const stringToCamelCase = (value) => {
    if (!value) return '';

    const exceptionalUpperCases = ['NPI', 'MID', 'CS'];

    // If the whole string is an exceptional case, return as is
    if (exceptionalUpperCases.includes(value)) {
        return value;
    }

    // Split by non-alphabetic characters or spaces for broader support
    const words = value.split(/[\s_-]+/);

    return words
        .map((word) => {
            // If the word is an exceptional uppercase case, return as is
            if (exceptionalUpperCases.includes(word.toUpperCase())) {
                return word.toUpperCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
};

/**
 *
 * @param value The value to be cleaned
 * @description This function cleans the input value by removing all non-alphanumeric characters and converting it to uppercase.
 * The function also limits the length of the input to 6 characters.
 * The cleaned value will be in the format of ABCDEF.
 * @example
 * cleanInput('abc123!@#') // returns 'ABC123'
 * @returns  {string} The cleaned value
 */
export const cleanInput = (val: string) => {
    return val
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, 6);
};

/**
 *
 * @param val The value to be formatted
 * @description This function formats the input value by adding dashes at specific positions.
 * The function will format the value to be in the format of AB-C1-23.
 * The function will add dashes after the 2nd and 4th characters.
 * @example
 * formatDashedInput('abc123') // returns 'AB-C1-23'
 * @returns  {string} The formatted value
 */
export const formatDashedInput = (val: string) => {
    if (val.length <= 2) return val;
    if (val.length <= 4) return `${val.slice(0, 2)}-${val.slice(2)}`;
    return `${val.slice(0, 2)}-${val.slice(2, 4)}-${val.slice(4)}`;
};
